import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ApplicantJob,
  jobApplicantPageFilters,
  candidatesPageFilters,
  JobApplications,
  JobDetails,
  jobPageFilters,
  TotalCount,
  ReportDetails,
} from "../types/jobTypes";

// Add this new type for scheduled interviews
export interface ScheduledInterview {
  id: number;
  jobId: number;
  date: string;
  start_time: string;
  status: number;
  timezone?: string;
  googleCalendarEventId?: string;
}

export interface JobState {
  details: JobDetails | null;
  jobsList: JobDetails[];
  demoJobsList: JobDetails[];
  demoJobsApplicantsList: JobDetails[];
  applicationResult: JobDetails | null;
  // Id (as a string) whose latest application-details request has finished, and
  // its error. `loading` is shared by every job request, so the profile waits on
  // this before it says the candidate couldn't be loaded.
  applicationSettledId: string | null;
  applicationError: string | null;
  reportDetails: ReportDetails | null;
  draftJobsList: JobDetails[];
  closedJobsList: JobDetails[];
  invitedJobsList: JobDetails[];
  pendingInterviewJobsList: JobDetails[];
  scheduledInterviewJobsList: JobDetails[];
  requestedJobsList: JobDetails[];
  recentJobsList: JobDetails[];
  applicantJobsList: ApplicantJob[];
  options: { [key: string]: string };
  currencies: { [key: string]: string };
  experience: { [key: number]: string } | null;
  applicantRecommendJobsList: ApplicantJob[];
  appliedJobsList: ApplicantJob[];
  jobApplicantsList: JobApplications[];
  allCandidatesList: JobApplications[];
  allCandidatesCount: number | null;
  allCandidatesOptions: { [key: string]: any };
  interviewId: number | null;
  scheduledInterviews: ScheduledInterview[]; // NEW: Track all scheduled interviews
  jobsCount: number | null;
  draftJobsCount: number | null;
  closedJobsCount: number | null;
  invitedJobsCount: number | null;
  pendingInterviewJobsCount: number | null;
  scheduledInterviewJobsCount: number | null;
  requestedJobsCount: number | null;
  isDraftSaved: boolean;
  totalCount: TotalCount;
  nextPage: string | null;
  previousPage: string | null;
  loading: boolean;
  draftLoading: boolean;
  error: string | null;
  jobListLoading: boolean;
  jobPageFilters: jobPageFilters;
  jobApplicantPageFilters: jobApplicantPageFilters;
  candidatesPageFilters: candidatesPageFilters;
  addons: any[] | null;
  // Filters of the latest job-page / My Candidates list request (no pagination),
  // recorded when it is sent, so the status cards count the same filtered set.
  jobApplicantsQuery: Record<string, string> | null;
  allCandidatesQuery: Record<string, string> | null;
  // Filters of the latest My Jobs list request (any tab; no paging / sort), so the
  // My Jobs KPI cards count candidates on exactly the listed jobs.
  myJobsListQuery: Record<string, string> | null;
}

const initialState: JobState = {
  details: null,
  jobsList: [],
  draftJobsList: [],
  demoJobsList: [],
  demoJobsApplicantsList: [],
  applicationResult: null,
  applicationSettledId: null,
  applicationError: null,
  reportDetails: null,
  closedJobsList: [],
  recentJobsList: [],
  invitedJobsList: [],
  pendingInterviewJobsList: [],
  scheduledInterviewJobsList: [],
  requestedJobsList: [],
  applicantJobsList: [],
  allCandidatesList: [],
  allCandidatesCount: 0,
  allCandidatesOptions: {},
  experience: {},
  options: {},
  currencies: {},
  applicantRecommendJobsList: [],
  appliedJobsList: [],
  jobApplicantsList: [],
  interviewId: 0,
  scheduledInterviews: [], // NEW: Initialize empty array
  jobsCount: 0,
  draftJobsCount: 0,
  closedJobsCount: 0,
  invitedJobsCount: 0,
  pendingInterviewJobsCount: 0,
  scheduledInterviewJobsCount: 0,
  requestedJobsCount: 0,
  totalCount: {
    total_all_jobs: 0,
    total_recommended_jobs: 0,
    total_applied_jobs: 0,
    total_pending_interviews: 0,
    total_scheduled_interviews: 0,
  },
  nextPage: null,
  previousPage: null,
  isDraftSaved: false,
  loading: false,
  draftLoading: false,
  error: null,
  jobListLoading: false,
  jobPageFilters: {
    current: 1,
    sortBy: "All",
    status: "All",
    searchInput: "",
    type: "All",
  },
  jobApplicantPageFilters: {
    current: 1,
    sortBy: "All",
    status: "All",
    stage: "All",
    searchInput: "",
    type: "All",
  },
  candidatesPageFilters: {
    current: 1,
    sortBy: "All",
    status: "All",
    stage: "All",
    searchInput: "",
  },
  addons: [],
  jobApplicantsQuery: null,
  allCandidatesQuery: null,
  myJobsListQuery: null,
};

const jobSlice = createSlice({
  name: "job",
  initialState,
  reducers: {
    //Set job list/details reducer
    setJobDetails: (state: JobState, action: PayloadAction<JobDetails>) => {
      state.details = action.payload as JobDetails;
    },
    setJobList: (state: JobState, action: PayloadAction<JobDetails[]>) => {
      state.jobsList = action.payload;
    },
    setDemoJobList: (state: JobState, action: PayloadAction<JobDetails[]>) => {
      state.demoJobsList = action.payload;
    },
    setDemoJobApplicantList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.demoJobsApplicantsList = action.payload;
    },
    setApplicationResult: (
      state: JobState,
      action: PayloadAction<JobDetails | null>
    ) => {
      state.applicationResult = action.payload;
    },
    setApplicationSettled: (
      state: JobState,
      action: PayloadAction<{ id: string | null; error: string | null }>
    ) => {
      state.applicationSettledId = action.payload.id;
      state.applicationError = action.payload.error;
    },
    setReportDetails: (state: JobState, action: PayloadAction<ReportDetails>) => {
      state.reportDetails = action.payload;
    },
    setDraftsJobList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.draftJobsList = action.payload;
    },
    setClosedJobList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.closedJobsList = action.payload;
    },
    setInvitedJobList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.invitedJobsList = action.payload;
    },
    setPendingInterviewJobList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.pendingInterviewJobsList = action.payload;
    },
    setScheduledInterviewJobList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.scheduledInterviewJobsList = action.payload;
    },
    setRequestedJobList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.requestedJobsList = action.payload;
    },
    setRecentJobList: (
      state: JobState,
      action: PayloadAction<JobDetails[]>
    ) => {
      state.recentJobsList = action.payload;
    },
    setApplicantJobsList: (
      state: JobState,
      action: PayloadAction<ApplicantJob[]>
    ) => {
      state.applicantJobsList = action.payload;
    },
    setRecommendJobsList: (
      state: JobState,
      action: PayloadAction<ApplicantJob[]>
    ) => {
      state.applicantRecommendJobsList = action.payload;
    },
    setAppliedJobsList: (
      state: JobState,
      action: PayloadAction<ApplicantJob[]>
    ) => {
      state.appliedJobsList = action.payload;
    },
    setJobApplicantsList: (
      state: JobState,
      action: PayloadAction<JobApplications[]>
    ) => {
      state.jobApplicantsList = action.payload;
    },
    setAllCandidatesList: (
      state: JobState,
      action: PayloadAction<JobApplications[]>
    ) => {
      state.allCandidatesList = action.payload;
    },
    setAllCandidatesCount: (state: JobState, action: PayloadAction<number>) => {
      state.allCandidatesCount = action.payload;
    },
    setAllCandidatesOptions: (
      state: JobState,
      action: PayloadAction<{ [key: string]: any }>
    ) => {
      state.allCandidatesOptions = action.payload;
    },
    setTotalCount: (state: JobState, action: PayloadAction<TotalCount>) => {
      state.totalCount = action.payload;
    },
    setNextPage: (state, action) => {
      state.nextPage = action.payload;
    },
    setPreviousPage: (state, action) => {
      state.previousPage = action.payload;
    },
    setOptionsList: (
      state: JobState,
      action: PayloadAction<{ [key: string]: string }>
    ) => {
      state.options = action.payload;
    },
    setAddonsList: (state: JobState, action: PayloadAction<any[]>) => {
      state.addons = action.payload;
    },

    //Update job list/details reducer
    updateJobDetails: (
      state: JobState,
      action: PayloadAction<Partial<JobDetails>>
    ) => {
      if (state.details === null) {
        state.details = action.payload as JobDetails;
      } else {
        state.details = { ...state.details, ...action.payload };
      }
    },
    updateJobList: (state, action: PayloadAction<JobDetails[]>) => {
      state.jobsList = state.jobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateDraftJobList: (state, action: PayloadAction<JobDetails[]>) => {
      state.draftJobsList = state.draftJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateClosedJobList: (state, action: PayloadAction<JobDetails[]>) => {
      state.closedJobsList = state.closedJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateInvitedJobList: (state, action: PayloadAction<JobDetails[]>) => {
      state.invitedJobsList = state.invitedJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateRequestJobList: (state, action: PayloadAction<JobDetails[]>) => {
      state.requestedJobsList = state.requestedJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateRecentJobList: (state, action: PayloadAction<JobDetails[]>) => {
      state.recentJobsList = state.recentJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateApplicantJobsList: (state, action: PayloadAction<ApplicantJob[]>) => {
      state.applicantJobsList = state.applicantJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateRecommendJobsList: (state, action: PayloadAction<ApplicantJob[]>) => {
      state.applicantRecommendJobsList = state.applicantRecommendJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },
    updateAppliedJobsList: (state, action: PayloadAction<ApplicantJob[]>) => {
      state.appliedJobsList = state.appliedJobsList.map(
        (job) =>
          action.payload.find((updatedJob) => updatedJob.id === job.id) || job
      );
    },

    // Update individual job application status/stage
    updateJobApplication: (
      state: JobState,
      action: PayloadAction<{ applicationId: number; status?: string; stage?: string }>
    ) => {
      const { applicationId, status, stage } = action.payload;
      
      // Update in jobApplicantsList
      state.jobApplicantsList = state.jobApplicantsList.map(app =>
        app.id === applicationId
          ? { 
              ...app, 
              ...(status !== undefined && { status }), 
              ...(stage !== undefined && { stage }) 
            }
          : app
      );
      
      // Update in allCandidatesList
      state.allCandidatesList = state.allCandidatesList.map(app =>
        app.id === applicationId
          ? { 
              ...app, 
              ...(status !== undefined && { status }), 
              ...(stage !== undefined && { stage }) 
            }
          : app
      );
    },

    // Reducer to set the total my jobs count
    setJobsCount: (state, action) => {
      state.jobsCount = action.payload;
    },

    setJobApplicantsQuery: (state, action: PayloadAction<Record<string, string> | null>) => {
      state.jobApplicantsQuery = action.payload;
    },

    setAllCandidatesQuery: (state, action: PayloadAction<Record<string, string> | null>) => {
      state.allCandidatesQuery = action.payload;
    },

    setMyJobsListQuery: (state, action: PayloadAction<Record<string, string> | null>) => {
      state.myJobsListQuery = action.payload;
    },

    // Reducer to set the total draft jobs count
    setDraftJobsCount: (state, action) => {
      state.draftJobsCount = action.payload;
    },

    // Reducer to set the total closed jobs count
    setClosedJobsCount: (state, action) => {
      state.closedJobsCount = action.payload;
    },
    setInvitedJobsCount: (state, action) => {
      state.invitedJobsCount = action.payload;
    },
    setPendingInterviewJobsCount: (state, action) => {
      state.pendingInterviewJobsCount = action.payload;
    },
    setScheduledInterviewJobsCount: (state, action) => {
      state.scheduledInterviewJobsCount = action.payload;
    },

    // Reducer to set the total closed jobs count
    setRequestedJobsCount: (state, action) => {
      state.requestedJobsCount = action.payload;
    },
    // Reducer to set the total closed jobs count
    setJobPageFilters: (state, action) => {
      state.jobPageFilters = action.payload || 0;
    },
    setJobApplicantPageFilters: (state, action) => {
      state.jobApplicantPageFilters = action.payload || 0;
    },
    setCandidatesPageFilters: (state, action) => {
      state.candidatesPageFilters = action.payload || 0;
    },
    // Reducer to set the interviewId
    setInterviewId: (state: JobState, action: PayloadAction<number>) => {
      state.interviewId = action.payload;
    },
    
    // NEW: Reducers for scheduled interviews
    setScheduledInterview: (state: JobState, action: PayloadAction<ScheduledInterview>) => {
      // Ensure scheduledInterviews is initialized
      if (!state.scheduledInterviews) {
        state.scheduledInterviews = [];
      }
      state.scheduledInterviews.push(action.payload);
      state.interviewId = action.payload.id;
    },
    setScheduledInterviews: (state: JobState, action: PayloadAction<ScheduledInterview[]>) => {
      state.scheduledInterviews = action.payload;
    },
    updateScheduledInterview: (state: JobState, action: PayloadAction<ScheduledInterview>) => {
      const index = state.scheduledInterviews.findIndex(interview => interview.id === action.payload.id);
      if (index !== -1) {
        state.scheduledInterviews[index] = action.payload;
      }
    },
    removeScheduledInterview: (state: JobState, action: PayloadAction<number>) => {
      state.scheduledInterviews = state.scheduledInterviews.filter(
        interview => interview.id !== action.payload
      );
    },
    
    setCurrencies: (
      state: JobState,
      action: PayloadAction<{ [key: string]: string }>
    ) => {
      state.currencies = action.payload;
    },
    setExperience: (
      state: JobState,
      action: PayloadAction<{ [key: string]: string }>
    ) => {
      state.experience = action.payload;
    },

    setJobLoading: (state: JobState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setJobListLoading: (state: JobState, action: PayloadAction<boolean>) => {
      state.jobListLoading = action.payload;
    },
    setDraftLoading: (state: JobState, action: PayloadAction<boolean>) => {
      state.draftLoading = action.payload;
    },
    setJobError: (state: JobState, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setDraftSaved: (state: JobState, action: PayloadAction<boolean>) => {
      state.isDraftSaved = action.payload;
    },

    resetJobDraftState: (state: JobState) => {
      state.details = initialState.details;
    },

    resetJobDetailsState: (state: JobState) => {
      state.details = initialState.details;
    },

    resetJobState: () => initialState,
  },
});

export const {
  setJobDetails,
  setJobList,
  setDraftsJobList,
  setDemoJobList,
  setDemoJobApplicantList,
  setApplicationResult,
  setApplicationSettled,
  setReportDetails,
  setClosedJobList,
  setInvitedJobList,
  setPendingInterviewJobList,
  setScheduledInterviewJobList,
  setRequestedJobList,
  setRecentJobList,
  updateInvitedJobList,
  updateRequestJobList,
  updateJobList,
  updateDraftJobList,
  updateClosedJobList,
  updateRecentJobList,
  updateJobDetails,
  setDraftSaved,
  setJobLoading,
  setJobListLoading,
  setDraftLoading,
  setJobError,
  resetJobState,
  resetJobDraftState,
  resetJobDetailsState,
  setApplicantJobsList,
  setJobApplicantsList,
  updateApplicantJobsList,
  setRecommendJobsList,
  setAppliedJobsList,
  updateRecommendJobsList,
  updateAppliedJobsList,
  setInterviewId,
  setScheduledInterview,  // NEW
  setScheduledInterviews, // NEW
  updateScheduledInterview, // NEW
  removeScheduledInterview, // NEW
  setJobsCount,
  setJobApplicantsQuery,
  setAllCandidatesQuery,
  setMyJobsListQuery,
  setTotalCount,
  setNextPage,
  setPreviousPage,
  setDraftJobsCount,
  setClosedJobsCount,
  setInvitedJobsCount,
  setPendingInterviewJobsCount,
  setScheduledInterviewJobsCount,
  setRequestedJobsCount,
  setOptionsList,
  setCurrencies,
  setJobPageFilters,
  setJobApplicantPageFilters,
  setCandidatesPageFilters,
  setAddonsList,
  setExperience,
  setAllCandidatesCount,
  setAllCandidatesList,
  setAllCandidatesOptions,
  updateJobApplication,
} = jobSlice.actions;
export default jobSlice.reducer;